import { ReactNode } from 'react';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { Cmp, trackAction } from '~portal/lib';

/** @desc Charlie renders message times as a bare 24h clock (e.g. "15:07"). */
export function formatMessageTime(value: Date | string | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export type PromptActionsProps = {
  /** Timestamp of the user's message. */
  timestamp?: Date | string;
};

/**
 * @desc Metadata row beneath a user message: right-aligned time (Figma →
 * Screens, response screen 223:37934).
 */
export const PromptActions: Cmp<PromptActionsProps> = function ({ timestamp }) {
  const time = formatMessageTime(timestamp);
  if (!time) return null;

  return (
    // `mt` is what separates the time from the bubble it belongs to. Without it the two line
    // boxes abut and the clock reads as part of the message rather than as a note under it.
    <Group gap={4} justify="flex-end" align="center" wrap="nowrap" mt="xs">
      <Text size="xs" c="blue.7">
        {time}
      </Text>
    </Group>
  );
};

export type ResponseActionsProps = {
  /** The response text, copied verbatim by the copy action. */
  text: string;
  /** Timestamp of the agent's reply. */
  timestamp?: Date | string;
  /** Extra controls rendered ahead of the copy action (the feedback thumbs, H2-205). */
  leading?: ReactNode;
  /**
   * Extra controls after the copy action — the files disclosure (ADR-22). It belongs *in* this
   * row rather than after it, because the row is the response's last element and the table it
   * toggles opens above it.
   */
  trailing?: ReactNode;
};

/**
 * @desc Action row beneath an agent response — thumbs, copy, then the time — left-aligned with
 * the response prose above it.
 *
 * The negative left margin is optical, not a gap: an `md` ActionIcon centres an 18px glyph in a
 * 34px hit area, so without cancelling that 8px the row's first icon reads as indented from the
 * text it belongs to.
 *
 * The row is the answer's own footer, set apart from the body rather than tucked under it: at
 * the original 4px the glyphs read as another line of the last paragraph. The gap above it is
 * its own margin plus whatever the last block carries.
 */
export const ResponseActions: Cmp<ResponseActionsProps> = function ({
  text,
  timestamp,
  leading,
  trailing,
}) {
  const clipboard = useClipboard({ timeout: 1500 });
  const time = formatMessageTime(timestamp);

  return (
    <Group justify="flex-start" gap={4} align="center" wrap="nowrap" mt="xs" ml={-8}>
      {/* The time leads the row: it is a label on the answer above, not a control, and reading
          it first keeps the controls themselves as one uninterrupted group. */}
      {time && (
        <Text size="xs" c="dimmed" mr="xs" ml={8}>
          {time}
        </Text>
      )}

      {leading}
      <Tooltip label={clipboard.copied ? 'Copied' : 'Copy response'} withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="md"
          aria-label="Copy response"
          data-dd-action-name="Copy response"
          onClick={() => {
            clipboard.copy(text);
            // Length, not the answer: what is worth knowing is whether long answers get copied.
            trackAction('response_copied', { length: text.length });
          }}
        >
          {clipboard.copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
        </ActionIcon>
      </Tooltip>

      {trailing}
    </Group>
  );
};
